import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MoneyAmount } from '@/components/MoneyAmount';
import { TokensTab } from '@/components/BatonTokensTab';
import { TrustTab } from '@/components/BatonTrustTab';
import { ActivityTab } from '@/components/BatonActivityTab';
import { BatonWarnings } from '@/components/BatonWarnings';
import { ApiError } from '@/lib/fetcher';
import {
  type BatonMetaData,
  type BatonWarning,
  addBatonTier,
  appendBaton,
  batonMetaKey,
  batonReadKey,
  deleteBaton,
  extendBaton,
  fetchBaton,
  fetchBatonMeta,
} from '@/lib/queries';
import {
  formatBytes,
  formatDurationShort,
  statusBadgeVariant,
} from '@/lib/baton-format';

const TIERS = ['femto', 'pico', 'nano', 'micro', 'standard', 'big'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export function BatonDetail() {
  const { id = '' } = useParams();
  const metaQ = useQuery({
    queryKey: batonMetaKey(id),
    queryFn: () => fetchBatonMeta(id),
  });

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/dashboard/batons">
            <ArrowLeft className="h-4 w-4" /> Batons
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {metaQ.data?.data.name ?? 'Baton'}
          </h1>
          {metaQ.data ? (
            <Badge variant={statusBadgeVariant(metaQ.data.data.deleteState)}>
              {metaQ.data.data.deleteState}
            </Badge>
          ) : null}
        </div>
        <p className="font-mono text-xs text-muted-foreground">{id}</p>
      </header>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="tokens">Tokens</TabsTrigger>
          <TabsTrigger value="trust">Trust</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {metaQ.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : metaQ.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load this baton (
                {metaQ.error instanceof ApiError ? metaQ.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                It may not exist, or it isn't yours.
              </AlertDescription>
            </Alert>
          ) : metaQ.data ? (
            <OverviewTab
              id={id}
              meta={metaQ.data.data}
              warnings={metaQ.data.warnings}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="content">
          <ContentTab id={id} meta={metaQ.data?.data} />
        </TabsContent>

        <TabsContent value="tokens">
          <TokensTab id={id} meta={metaQ.data?.data} />
        </TabsContent>
        <TabsContent value="trust">
          <TrustTab id={id} meta={metaQ.data?.data} />
        </TabsContent>
        <TabsContent value="activity">
          <ActivityTab id={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewTab({
  id,
  meta,
  warnings,
}: {
  id: string;
  meta: BatonMetaData;
  warnings: BatonWarning[];
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: batonMetaKey(id) });
    void qc.invalidateQueries({ queryKey: ['batons', 'list'] });
  };

  const remove = useMutation({
    mutationFn: () => deleteBaton(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['batons'] });
      navigate('/dashboard/batons');
    },
  });

  const r = meta.bundleResources;
  const c = meta.calculated;
  const isActive = meta.deleteState === 'active';

  return (
    <div className="space-y-6">
      <BatonWarnings warnings={warnings} />
      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Type">
              <span className="capitalize">{meta.batonType}</span>
            </Field>
            <Field label="Preset · Tier">
              <span className="capitalize">{r.preset}</span> · {r.tier}
            </Field>
            <Field label="Provisioned price">
              <MoneyAmount micros={r.priceMicros} minorDigits={4} />
            </Field>
            <Field label="Created">
              {format(parseISO(meta.createdAt), 'MMM d, yyyy HH:mm')}
            </Field>
            <Field label="Expires">
              {format(parseISO(c.expiresAt), 'MMM d, yyyy HH:mm')}{' '}
              <span className="text-muted-foreground">
                ({formatDurationShort(c.secondsRemaining)})
              </span>
            </Field>
            <Field label="Trust state">
              <Badge
                variant={meta.trustState === 'witnessed' ? 'success' : 'secondary'}
              >
                {meta.trustState ?? 'open'}
              </Badge>
            </Field>
            <Field label="Flags">
              <div className="flex flex-wrap gap-1">
                {meta.hashChaining ? (
                  <Badge variant="secondary">hash-chained</Badge>
                ) : null}
                {meta.trustState === 'witnessed' ? (
                  <Badge variant="secondary">witnessed</Badge>
                ) : null}
                {!meta.hashChaining && meta.trustState !== 'witnessed' ? (
                  <span className="text-muted-foreground">—</span>
                ) : null}
              </div>
            </Field>
            <Field label="Visibility">{meta.visibility}</Field>
            <Field label="On expiration">{meta.onExpiration}</Field>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Resources</CardTitle>
          <CardDescription>Remaining against the provisioned bundle.</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Size left">
              {formatBytes(c.bytesRemaining)}{' '}
              <span className="text-muted-foreground">
                of {formatBytes(r.maxSizeBytes)}
              </span>
            </Field>
            <Field label="Egress left">
              {c.egressRemainingGb.toFixed(3)} GB
            </Field>
            <Field label="Writes left">
              {c.writesRemaining === null ? 'unlimited' : c.writesRemaining}
            </Field>
            <Field label="Active tokens">{c.activeTokenCount}</Field>
            {meta.batonType === 'ledger' && c.chainLength !== null ? (
              <Field label="Chain length">{c.chainLength}</Field>
            ) : null}
            <Field label="Est. remaining value">
              {c.estimatedRemainingValueCents === null
                ? '—'
                : `$${(c.estimatedRemainingValueCents / 100).toFixed(2)}`}
            </Field>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lifecycle</CardTitle>
          <CardDescription>
            Buy more capacity, or delete this baton.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <ExtendDialog id={id} onDone={refresh} disabled={!isActive} />
          <AddTierDialog id={id} onDone={refresh} disabled={!isActive} />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={!isActive}>
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this baton?</AlertDialogTitle>
                <AlertDialogDescription>
                  It enters its tombstone window and its content becomes
                  inaccessible. This can't be undone from the dashboard.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={remove.isPending}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    remove.mutate();
                  }}
                  disabled={remove.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}

function ExtendDialog({
  id,
  onDone,
  disabled,
}: {
  id: string;
  onDone: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [sizeKb, setSizeKb] = React.useState('');
  const [days, setDays] = React.useState('');
  const [egressMb, setEgressMb] = React.useState('');
  const [writes, setWrites] = React.useState('');

  const m = useMutation({
    mutationFn: () => {
      const body: Record<string, number> = {};
      if (Number(sizeKb) > 0) body.addSizeBytes = Math.round(Number(sizeKb) * 1000);
      if (Number(days) > 0) body.addDurationSeconds = Math.round(Number(days) * 86400);
      if (Number(egressMb) > 0) body.addEgressBytes = Math.round(Number(egressMb) * 1_000_000);
      if (Number(writes) > 0) body.addWritesAllowed = Math.round(Number(writes));
      return extendBaton(id, body);
    },
    onSuccess: () => {
      onDone();
      setOpen(false);
      setSizeKb('');
      setDays('');
      setEgressMb('');
      setWrites('');
    },
  });

  const anyField =
    Number(sizeKb) > 0 ||
    Number(days) > 0 ||
    Number(egressMb) > 0 ||
    Number(writes) > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          Extend
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Extend à-la-carte</DialogTitle>
          <DialogDescription>
            Buy more of any dimension. Billable — priced at the engine rate,
            floored at the network minimum.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="ext-size">Add size (KB)</Label>
            <Input id="ext-size" type="number" min="0" value={sizeKb} onChange={(e) => setSizeKb(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ext-days">Add duration (days)</Label>
            <Input id="ext-days" type="number" min="0" value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ext-egress">Add egress (MB)</Label>
            <Input id="ext-egress" type="number" min="0" value={egressMb} onChange={(e) => setEgressMb(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ext-writes">Add writes</Label>
            <Input id="ext-writes" type="number" min="0" value={writes} onChange={(e) => setWrites(e.target.value)} />
          </div>
        </div>
        {m.error ? (
          <Alert variant="destructive">
            <AlertTitle>
              {m.error instanceof ApiError ? m.error.code : 'Failed'}
            </AlertTitle>
            <AlertDescription>
              {m.error instanceof ApiError ? m.error.message : 'Try again.'}
            </AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button
            onClick={() => m.mutate()}
            disabled={!anyField || m.isPending}
          >
            {m.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Extending…
              </>
            ) : (
              'Extend'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddTierDialog({
  id,
  onDone,
  disabled,
}: {
  id: string;
  onDone: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [tier, setTier] = React.useState('femto');

  const m = useMutation({
    mutationFn: () => addBatonTier(id, tier),
    onSuccess: () => {
      onDone();
      setOpen(false);
      setTier('femto');
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          Add tier
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a bundle tier</DialogTitle>
          <DialogDescription>
            Buy another catalog tier of this baton's preset — additive across
            every dimension. Billable.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="add-tier">Tier</Label>
          <select
            id="add-tier"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        {m.error ? (
          <Alert variant="destructive">
            <AlertTitle>
              {m.error instanceof ApiError ? m.error.code : 'Failed'}
            </AlertTitle>
            <AlertDescription>
              {m.error instanceof ApiError ? m.error.message : 'Try again.'}
            </AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button onClick={() => m.mutate()} disabled={m.isPending}>
            {m.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Adding…
              </>
            ) : (
              'Add tier'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContentTab({ id, meta }: { id: string; meta?: BatonMetaData }) {
  const qc = useQueryClient();
  // No auto-fetch: GET /v1/baton/:id meters egress (bundle_consumed.egressBytes
  // += content size, cap-enforced). The read fires ONLY when the customer
  // clicks the explicit action below — opening the tab must never silently
  // spend egress. Richer egress surfacing is P8-4.
  const [retrieve, setRetrieve] = React.useState(false);
  const q = useQuery({
    queryKey: batonReadKey(id),
    queryFn: () => fetchBaton(id),
    enabled: retrieve,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  // A successful read drew down egress server-side — refresh the (free) meta
  // so Overview's "Egress left" reflects the spend.
  React.useEffect(() => {
    if (q.isSuccess) {
      void qc.invalidateQueries({ queryKey: batonMetaKey(id) });
    }
  }, [q.isSuccess, id, qc]);

  const [appendText, setAppendText] = React.useState('');
  const append = useMutation({
    mutationFn: () =>
      appendBaton(id, {
        content: appendText,
        contentEncoding: 'utf-8',
        contentType: 'text/plain',
      }),
    onSuccess: () => {
      setAppendText('');
      // Collapse the now-stale retrieved view; a fresh read is a deliberate
      // (egress-consuming) re-click. Size/egress changed → refresh meta.
      setRetrieve(false);
      void qc.invalidateQueries({ queryKey: batonMetaKey(id) });
    },
  });

  let hasContent = true;
  if (meta?.sizeBytes !== undefined) {
    try {
      hasContent = BigInt(meta.sizeBytes) > 0n;
    } catch {
      hasContent = true;
    }
  }
  const isTextLikely = meta?.contentEncoding === 'utf-8';

  const gone = q.error instanceof ApiError && q.error.status === 410;
  const tooLargeInline =
    q.error instanceof ApiError && q.error.status === 413;
  const egressExhausted =
    q.error instanceof ApiError && q.error.code === 'EGRESS_EXHAUSTED';
  const data = q.data?.data;
  const downloadUrl = q.data?.meta?.presignedDownloadUrl;
  const isText = data?.contentEncoding === 'utf-8';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Content</CardTitle>
          <CardDescription>
            {meta
              ? `${meta.contentType} · ${formatBytes(meta.sizeBytes)}`
              : 'Stored content.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!meta ? (
            <Skeleton className="h-24 w-full" />
          ) : !hasContent ? (
            <p className="text-sm text-muted-foreground">
              No content yet. Append below.
            </p>
          ) : !retrieve ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Reading content draws down this baton's egress allotment.
              </p>
              <Button onClick={() => setRetrieve(true)}>
                {isTextLikely ? (
                  'Retrieve content'
                ) : (
                  <>
                    <Download className="h-4 w-4" /> Download content
                  </>
                )}
              </Button>
            </div>
          ) : q.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Retrieving…
            </div>
          ) : gone ? (
            <Alert>
              <AlertTitle>Content gone</AlertTitle>
              <AlertDescription>
                This baton has been deleted or expired; its content is no
                longer available.
              </AlertDescription>
            </Alert>
          ) : egressExhausted ? (
            <Alert variant="destructive">
              <AlertTitle>Egress allowance exhausted</AlertTitle>
              <AlertDescription>
                Extend this baton's egress to read it again.
              </AlertDescription>
            </Alert>
          ) : tooLargeInline ? (
            <Alert>
              <AlertTitle>Too large to display inline</AlertTitle>
              <AlertDescription>
                Use the entries view (coming soon) to page through this baton.
              </AlertDescription>
            </Alert>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load content (
                {q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
            </Alert>
          ) : downloadUrl ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Ready to download ({formatBytes(data?.sizeBytes ?? '0')}).
              </p>
              <Button asChild variant="outline">
                <a href={downloadUrl}>
                  <Download className="h-4 w-4" /> Download
                </a>
              </Button>
            </div>
          ) : data?.content && isText ? (
            <pre className="max-h-96 overflow-auto rounded-md border bg-muted/40 p-3 text-xs leading-5 whitespace-pre-wrap">
              {data.content}
            </pre>
          ) : data?.content ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Binary content ({formatBytes(data.sizeBytes)}).
              </p>
              <Button asChild variant="outline">
                <a
                  href={`data:${data.contentType};base64,${data.content}`}
                  download
                >
                  <Download className="h-4 w-4" /> Download
                </a>
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No content returned.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Append</CardTitle>
          <CardDescription>
            Add an entry. On CHECKPOINT batons this overwrites; on chained
            presets it appends.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            value={appendText}
            onChange={(e) => setAppendText(e.target.value)}
            rows={4}
            placeholder="Text to append…"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
          />
          {append.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                {append.error instanceof ApiError ? append.error.code : 'Failed'}
              </AlertTitle>
              <AlertDescription>
                {append.error instanceof ApiError
                  ? append.error.message
                  : 'Try again.'}
              </AlertDescription>
            </Alert>
          ) : null}
          <Button
            onClick={() => append.mutate()}
            disabled={!appendText || append.isPending}
          >
            {append.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Appending…
              </>
            ) : (
              'Append'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
